/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de TRABALHOS (`/trabalhos`).
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters` e do
 * `clientsListFilters`: são regras que decidem QUAIS trabalhos o prestador vê e
 * em que ordem, e todas erram em silêncio — uma lista recortada errado não dá
 * erro, só esconde serviço em aberto.
 *
 * ─── O QUE É ESPECÍFICO DESTA LISTA ─────────────────────────────────────────
 *
 * · **A entidade é DERIVADA, não uma linha de tabela.** Cada entrada é um
 *   `WorkListEntry` montado por `buildWorkList` a partir de cinco tabelas, então
 *   aqui não existe `created_at`: a data do trabalho é `entry.sortDate` (a do
 *   documento MAIS ANTIGO do grupo — quando o serviço começou) e o valor é
 *   `entry.work.total`. Ler `created_at` de um `entry` compilaria (é `any` em
 *   nenhum lugar, mas o campo simplesmente não existe) e recortaria tudo.
 * · **`lost` é o status morto desta tela.** O hero soma dinheiro, e um trabalho
 *   perdido é dinheiro que não vai entrar — somá-lo diria ao prestador que ele
 *   tem mais na mão do que tem (o corolário do #182/#188). Quantos ficaram fora
 *   é DEVOLVIDO para a tela dizer: "no silent caps".
 * · **O recorte por FASE depende da aba.** A aba "Em aberto" (`isOpenWork`) já
 *   exclui `paid` e `lost`; oferecer botões dessas duas fases ali produziria um
 *   recorte garantidamente vazio, com o usuário sem entender por quê.
 *
 * O parser de dinheiro é o mesmo `quoteTotalNumber` (#123a) e a normalização de
 * texto é a mesma da busca de documentos (#190d): dobra caixa E acento, senão
 * quem digita "manutencao" no teclado do celular recebe zero por "Manutenção".
 */
import {
  WORK_PHASE_LABELS,
  isOpenWork,
  type WorkListEntry,
  type WorkPhase,
} from '@/lib/work';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { normalizeSearchText } from '@/lib/quoteSearch';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

/** As duas abas da tela: só o que está em andamento, ou o histórico inteiro. */
export type WorkScope = 'open' | 'all';

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: título do trabalho e nome do cliente — que é exatamente o
 * que o card mostra. Buscar em campo que a linha não exibe devolveria
 * resultados que o usuário não consegue explicar.
 */
export function workMatchesSearch(entry: WorkListEntry, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;
  if (normalizeSearchText(entry.work?.title).includes(needle)) return true;
  if (normalizeSearchText(entry.work?.clientName).includes(needle)) return true;
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type WorkSortOption = 'recent' | 'oldest' | 'highest' | 'lowest' | 'client' | 'title';

export const WORK_SORT_LABELS: Record<WorkSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  client: 'Nome do cliente',
  title: 'Título do trabalho',
};

export const WORK_SORT_ORDER: readonly WorkSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'client',
  'title',
];

/**
 * `recent` é o PADRÃO porque é como a lista sempre se comportou
 * (`buildWorkList` já devolve o mais recente primeiro). Trocar o padrão junto
 * com o redesenho mudaria a tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_WORK_SORT: WorkSortOption = 'recent';

/**
 * Data ilegível/ausente vai para o FIM em "mais recentes" — trabalho sem data é
 * exceção, e exceção não ocupa a primeira linha da lista.
 */
function timeOf(entry: WorkListEntry): number {
  const time = entry.sortDate?.getTime();
  return typeof time === 'number' && Number.isFinite(time) ? time : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem título" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function totalOf(entry: WorkListEntry): number {
  return quoteTotalNumber(entry.work?.total);
}

/** Não muta a entrada: a lista da tela é derivada. */
export function sortWorksBy(
  entries: readonly WorkListEntry[],
  option: WorkSortOption,
): WorkListEntry[] {
  const rows = [...entries];
  // Desempate ESTÁVEL por id (#93): sem ele, dois trabalhos do mesmo dia (ou do
  // mesmo valor — valores redondos repetem muito) trocam de lugar entre
  // renders e a lista "pisca" sem ninguém ter mexido em nada.
  const byId = (a: WorkListEntry, b: WorkListEntry) =>
    String(a.root.id).localeCompare(String(b.root.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'highest':
      return rows.sort((a, b) => totalOf(b) - totalOf(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => totalOf(a) - totalOf(b) || byId(a, b));
    case 'client':
      return rows.sort(
        (a, b) => textOf(a.work?.clientName).localeCompare(textOf(b.work?.clientName)) || byId(a, b),
      );
    case 'title':
      return rows.sort(
        (a, b) => textOf(a.work?.title).localeCompare(textOf(b.work?.title)) || byId(a, b),
      );
    case 'recent':
    default:
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
  }
}

// ─── Recorte por FASE (os botões da faixa) ──────────────────────────────────

export type WorkPhaseFilter = 'all' | WorkPhase;

/**
 * Rótulos CURTOS para os botões da faixa.
 *
 * Os nomes completos ("Aguardando resposta", "Negócio fechado") são os do
 * cabeçalho de cada grupo, onde há a largura da tela inteira. Numa faixa de
 * botões eles somariam bem mais que os 328px úteis de um aparelho de 360 e a
 * barra rolaria muito — o mesmo cálculo do #191, em que o que não cabia era a
 * contagem e não o texto. Aqui a contagem também vai no `title`/`aria-label`.
 */
export const WORK_PHASE_SHORT_LABELS: Record<WorkPhase, string> = {
  quoting: 'Aguardando',
  closed: 'Fechado',
  executing: 'Execução',
  delivered: 'Concluído',
  paid: 'Pago',
  lost: 'Perdido',
};

/** As fases que a aba "Em aberto" pode conter — `isOpenWork` exclui as outras. */
const OPEN_PHASES: readonly WorkPhase[] = ['executing', 'closed', 'quoting', 'delivered'];
const ALL_PHASES: readonly WorkPhase[] = [...OPEN_PHASES, 'paid', 'lost'];

/**
 * Os botões da faixa, na ordem do fluxo — e dependentes da ABA.
 *
 * Em "Em aberto" não há botão de `paid` nem de `lost`: eles trariam ZERO
 * sempre, porque a aba já os excluiu. Um botão que promete um recorte vazio é
 * pior do que botão nenhum (#91).
 */
export function workPhaseFilters(
  scope: WorkScope,
): readonly { value: WorkPhaseFilter; label: string }[] {
  const phases = scope === 'open' ? OPEN_PHASES : ALL_PHASES;
  return [
    { value: 'all' as WorkPhaseFilter, label: 'Todos' },
    ...phases.map((phase) => ({
      value: phase as WorkPhaseFilter,
      label: WORK_PHASE_SHORT_LABELS[phase],
    })),
  ];
}

/**
 * `true` quando o recorte escolhido continua existindo na aba.
 *
 * Trocar para "Em aberto" com o filtro em "Pago" deixaria a tela vazia sem
 * nenhum controle explicando por quê — a página usa isto para voltar a "Todos".
 */
export function phaseFilterFitsScope(filter: WorkPhaseFilter, scope: WorkScope): boolean {
  if (filter === 'all') return true;
  return scope === 'all' || OPEN_PHASES.includes(filter);
}

export function matchesWorkPhase(entry: WorkListEntry, filter: WorkPhaseFilter): boolean {
  return filter === 'all' || entry.work?.phase === filter;
}

/**
 * Quantos trabalhos cada botão traria — contados sobre o recorte JÁ filtrado
 * por aba, busca, período e valor, mas ANTES da fase.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 com uma busca ativa que devolve 3, e contar depois da fase
 * zeraria todos os outros botões (#193/#196).
 */
export function countWorksByPhase(
  entries: readonly WorkListEntry[],
): Record<WorkPhaseFilter, number> {
  const counts: Record<WorkPhaseFilter, number> = {
    all: entries.length,
    quoting: 0,
    closed: 0,
    executing: 0,
    delivered: 0,
    paid: 0,
    lost: 0,
  };
  for (const entry of entries) {
    const phase = entry.work?.phase;
    if (phase && phase in counts) counts[phase] += 1;
  }
  return counts;
}

/** A aba: só o que está em andamento (`isOpenWork`) ou tudo. */
export function matchesWorkScope(entry: WorkListEntry, scope: WorkScope): boolean {
  return scope === 'all' || isOpenWork(entry.work);
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/**
 * Presets oferecidos no modal. Subconjunto EXPLÍCITO dos de `reportPeriod`, o
 * mesmo de `/quotes` e `/clients`: os longos de relatório não ajudam a achar um
 * serviço, e `custom` exigiria um calendário dentro do modal.
 */
export const WORK_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

/**
 * `range` nulo = sem filtro. Trabalho SEM data legível NÃO é recortado: sumir
 * com ele por causa de um carimbo estranho é pior do que mostrá-lo fora da
 * faixa pedida — o usuário não teria como saber que ele existe (#193-d).
 */
export function matchesWorkPeriod(entry: WorkListEntry, range: ReportDateRange | null): boolean {
  if (!range) return true;
  const time = entry.sortDate?.getTime();
  if (typeof time !== 'number' || !Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesWorkValue(entry: WorkListEntry, range: ListValueRange): boolean {
  return matchesListRange(entry.work?.total, range);
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * A FASE fica de fora: ela tem os botões visíveis logo acima, então já se
 * anuncia sozinha. Contá-la faria o selo dizer "1" com o modal em branco.
 */
export function countActiveWorkFilters(
  period: ReportPeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** A fase que NÃO representa dinheiro vivo. */
export const DEAD_WORK_PHASES: ReadonlySet<string> = new Set<WorkPhase>(['lost']);

/** `true` quando o recorte É exatamente a fase morta (ver `includeDead`). */
export function isDeadWorkPhaseFilter(filter: WorkPhaseFilter): boolean {
  return DEAD_WORK_PHASES.has(filter);
}

export interface WorkListSummary {
  /** Trabalhos no recorte — todos, inclusive os perdidos. */
  count: number;
  /** Soma dos que ainda representam dinheiro (fora os perdidos). */
  total: number;
  /** Quantos ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeWorksOptions {
  /**
   * Soma TAMBÉM os perdidos.
   *
   * Existe por causa do filtro explícito por "Perdido": excluir todo mundo ali
   * faria o hero anunciar R$ 0,00 com a lista cheia embaixo — o número mais
   * visível da tela contradizendo o que ela mostra. A exclusão serve para a aba
   * "Todos" não INFLAR o que o prestador tem a receber; quando ele pede
   * justamente os perdidos, a pergunta mudou e a resposta honesta é quanto se
   * perdeu.
   */
  includeDead?: boolean;
}

export function summarizeWorkList(
  entries: readonly WorkListEntry[],
  options: SummarizeWorksOptions = {},
): WorkListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const entry of entries) {
    if (!options.includeDead && DEAD_WORK_PHASES.has(String(entry.work?.phase))) {
      excludedCount += 1;
      continue;
    }
    total += quoteTotalNumber(entry.work?.total);
  }

  return { count: entries.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto.
 *
 * A soma sai do recorte já filtrado (aba + busca + fase + período + valor),
 * então ela muda enquanto o usuário digita. Sem dizer de qual recorte ela saiu,
 * "R$ 12.400,00" sob o título "Trabalhos" lê como "é isso que eu tenho", quando
 * pode ser a fase de execução com uma busca por "João" ativa (#182/#188).
 */
export function workListTotalLabel(scope: WorkScope, phaseFilter: WorkPhaseFilter): string {
  if (phaseFilter !== 'all') {
    // A fase morta ganha rótulo PRÓPRIO porque, sob o filtro dela, a soma passa
    // a incluí-la (`includeDead`): dizer "Total em trabalhos" ali faria o número
    // parecer receita quando ele é justamente o oposto.
    if (phaseFilter === 'lost') return 'Total perdido';
    return `Total em ${WORK_PHASE_SHORT_LABELS[phaseFilter].toLowerCase()}`;
  }
  return scope === 'open' ? 'Total em aberto' : 'Total em trabalhos';
}

/** O rótulo do grupo, para a tela não recriar o mapa. */
export function workPhaseLabel(phase: WorkPhase): string {
  return WORK_PHASE_LABELS[phase];
}
